import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Eye, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { rpc } from '@/api/rpc'
import { Select } from '@/components/form/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { Field, Input } from '@/components/ui/field'
import { DemoDataCard } from '@/features/admin/demo-data-card'
import { formatWhen } from '@/lib/format'
import { supabase, viewAs } from '@/lib/supabase'

type UserRow = {
  id: string
  full_name: string
  display_name: string
  role: string
  email: string
  phone: string | null
  active: boolean
  region_id: string | null
  region: { code: string; name: string } | null
  last_login: string | null
}

const usersQuery = queryOptions({
  queryKey: ['admin', 'users'],
  queryFn: () => rpc<UserRow[]>('admin_list_users'),
})
const rolesQuery = queryOptions({
  queryKey: ['roles'],
  queryFn: async () => {
    const { data, error } = await supabase
      .from('roles')
      .select('key, label, sort_order')
      .order('sort_order')
    if (error) throw error
    return data as { key: string; label: string }[]
  },
  staleTime: 60 * 60_000,
})
const regionsQuery = queryOptions({
  queryKey: ['regions'],
  queryFn: async () => {
    const { data, error } = await supabase.from('regions').select('id, code, name').order('name')
    if (error) throw error
    return data as { id: string; code: string; name: string }[]
  },
  staleTime: 60 * 60_000,
})

export const Route = createFileRoute('/_app/admin/')({ component: Users })

function Users() {
  const { me } = Route.useRouteContext()
  const users = useQuery(usersQuery)
  const roles = useQuery(rolesQuery)
  const regions = useQuery(regionsQuery)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [inviting, setInviting] = useState(false)

  const update = useMutation({
    mutationFn: (args: Record<string, unknown>) => rpc('admin_update_user', args),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })

  const startViewAs = async (user: UserRow) => {
    await supabase.rpc('admin_log_view_as', { target: user.id, started: true })
    viewAs.set(user.id)
    queryClient.clear()
    await navigate({ to: '/' })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl uppercase">Users</h1>
        <Button onClick={() => setInviting((v) => !v)}>
          <UserPlus aria-hidden /> Invite someone
        </Button>
      </div>
      <DemoDataCard
        demoPeople={
          (users.data ?? []).filter((u) => u.email.endsWith('@demo.mezsites.test')).length
        }
      />
      {inviting && (
        <InviteForm
          roles={roles.data ?? []}
          regions={regions.data ?? []}
          onDone={() => setInviting(false)}
        />
      )}
      {(users.error || update.error) && (
        <p role="alert" className="font-semibold text-danger">
          {(users.error ?? update.error)?.message}
        </p>
      )}
      <Card
        className="relative w-full max-w-full min-w-0 overflow-x-auto p-0"
        tabIndex={0}
        role="region"
        aria-label="Users (scrolls sideways)"
      >
        <table className="w-full min-w-[960px] text-left">
          <thead className="border-b border-border text-sm text-fg-muted uppercase">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Region</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Last sign-in</th>
              <th className="px-4 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <div className="font-semibold">{u.display_name}</div>
                  <div className="text-sm text-fg-muted">{u.full_name}</div>
                  {!u.active && <Badge tone="danger">Inactive</Badge>}
                </td>
                <td className="px-4 py-3">
                  <Select
                    aria-label={`Role for ${u.display_name}`}
                    value={u.role}
                    disabled={u.id === me.id}
                    onChange={(e) => update.mutate({ user_id: u.id, new_role: e.target.value })}
                    options={(roles.data ?? []).map((r) => ({ value: r.key, label: r.label }))}
                    className="w-44"
                  />
                </td>
                <td className="px-4 py-3">
                  <Select
                    aria-label={`Region for ${u.display_name}`}
                    value={u.region_id ?? ''}
                    placeholder="All regions"
                    onChange={(e) =>
                      update.mutate(
                        e.target.value
                          ? { user_id: u.id, new_region_id: e.target.value }
                          : { user_id: u.id, clear_region: true },
                      )
                    }
                    options={(regions.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
                    className="w-44"
                  />
                </td>
                <td className="px-4 py-3 text-sm">
                  <div>{u.email}</div>
                  <div className="text-fg-muted">{u.phone}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {u.last_login ? formatWhen(u.last_login) : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    {u.id !== me.id && u.active && (
                      <Button size="sm" variant="outline" onClick={() => void startViewAs(u)}>
                        <Eye aria-hidden /> View as
                      </Button>
                    )}
                    {u.id !== me.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className={u.active ? 'text-danger' : undefined}
                        onClick={() => update.mutate({ user_id: u.id, new_active: !u.active })}
                      >
                        {u.active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function InviteForm({
  roles,
  regions,
  onDone,
}: {
  roles: { key: string; label: string }[]
  regions: { code: string; name: string }[]
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const [v, setV] = useState({
    full_name: '',
    display_name: '',
    email: '',
    phone: '',
    role: 'rep',
    region_code: '',
  })
  const invite = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('admin-users', {
        body: { action: 'invite', ...v, region_code: v.region_code || null },
      })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      onDone()
    },
  })
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) =>
    setV({ ...v, [k]: e.target.value })
  return (
    <Card className="flex flex-col gap-4">
      <CardTitle>Invite someone</CardTitle>
      <p className="text-fg-muted">
        They get an email to set their password. Email must be switched on (Resend domain verified).
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name">
          {(f) => <Input {...f} value={v.full_name} onChange={set('full_name')} />}
        </Field>
        <Field label="Short name (shown in the queue)">
          {(f) => <Input {...f} value={v.display_name} onChange={set('display_name')} />}
        </Field>
        <Field label="Email">
          {(f) => <Input {...f} type="email" value={v.email} onChange={set('email')} />}
        </Field>
        <Field label="Phone">
          {(f) => <Input {...f} type="tel" value={v.phone} onChange={set('phone')} />}
        </Field>
        <Field label="Role">
          {(f) => (
            <Select
              {...f}
              value={v.role}
              onChange={set('role')}
              options={roles.map((r) => ({ value: r.key, label: r.label }))}
            />
          )}
        </Field>
        <Field label="Region">
          {(f) => (
            <Select
              {...f}
              value={v.region_code}
              onChange={set('region_code')}
              options={regions.map((r) => ({ value: r.code, label: r.name }))}
              placeholder="All regions"
            />
          )}
        </Field>
      </div>
      {invite.error && (
        <p role="alert" className="font-semibold text-danger">
          {invite.error.message}
        </p>
      )}
      <Button
        className="self-start"
        disabled={!v.email || !v.full_name || invite.isPending}
        onClick={() => invite.mutate()}
      >
        Send invite
      </Button>
    </Card>
  )
}
